export default async function normalizeValues(
	userHierarchy: any[],
	links: { sourceUser: string; targetUser: string }[], // All links: user-to-user and user-to-department
	rootUser: any,
	departments: any[], // Handle departments being possibly empty
	width: number,
	height: number
): Promise<{
	users: any[];
	departments: any[];
}> {
	return new Promise((resolve, reject) => {
		try {
			const OFFSET_X = 400;
			const OFFSET_Y = 400;

			// Step 1: Initialize maps for users and departments
			let nodeMap: { [key: string]: any } = {};

			console.log('Root User:', rootUser);

			if(rootUser && rootUser._id) {
				nodeMap[rootUser._id] = {
					...rootUser,
					children: [],
					position: { x: null, y: null },
					type: 'user',
				};
			}

			// Add users to the node map
			console.log('📊 normalizeValues - Processing userHierarchy:', {
				totalUsers: userHierarchy.length,
				hasRootUser: !!rootUser,
				rootUserId: rootUser?._id
			});
			
			userHierarchy.forEach((user, index) => {
				if (user && user.user && user.user._id) {
					console.log('✅ Adding user to nodeMap:', {
						index,
						userId: user.user._id,
						userEmail: user.user.email,
						userName: user.user.profile_information?.name,
						userDepartment: user.user.profile_information?.department
					});
					nodeMap[user.user._id] = {
						...user,
						children: [],
						position: { x: null, y: null },
						type: 'user',
					};
				} else {
					console.error('❌ User without valid _id:', {
						user,
						hasUser: !!user?.user,
						hasUserId: !!user?.user?._id
					});
				}
			});


			// Only add departments to the node map if the departments array is not empty
			if (departments.length > 0) {
				departments.forEach((dept) => {
					if (dept && dept._id) {
						nodeMap[dept._id] = {
							...dept,
							children: [],
							position: { x: null, y: null },
							type: 'department',
						};
					} else {
						console.error('Department without valid _id:', dept);
					}
				});
			}

			// Step 2: Build parent-child relationships from links
			links?.forEach((link) => {
				const sourceNode = nodeMap[link.sourceUser];
				const targetNode = nodeMap[link.targetUser];

				if (sourceNode && targetNode) {
					sourceNode.children.push(targetNode);
				} else {
					console.error(
						'Invalid link, source or target node not found:',
						link
					);
				}
			});

			// Step 3: Queue-based processing for positioning
			const queue: { node: any; x: number; y: number; level: number }[] =
				[
					{
						node: nodeMap[rootUser._id],
						x: 0,
						y: 0,
						level: 0,
					},
				];

			const unresolvedNodes = new Set<string>();

			while (queue.length > 0 || unresolvedNodes.size > 0) {
				if (queue.length === 0) {
					// Process unresolved nodes once dependencies are resolved
					for (const nodeId of Array.from(unresolvedNodes)) {
						const unresolvedNode = nodeMap[nodeId];

						// Ensure unresolvedNode is valid
						if (!unresolvedNode) {
							console.error(
								`Unresolved node not found in nodeMap: ${nodeId}`
							);
							continue;
						}

						// Check if the parent has a position set
						const parentLink = links.find(
							(link) => link.targetUser === nodeId
						);
						const parentNode = parentLink
							? nodeMap[parentLink.sourceUser]
							: null;

						if (!parentNode) {
							console.error(
								`Parent node not found for link: ${parentLink}`
							);
						}

						if (parentNode && parentNode.position?.x !== null) {
							unresolvedNodes.delete(nodeId);
							queue.push({
								node: unresolvedNode,
								x: parentNode.position.x,
								y: parentNode.position.y + OFFSET_Y,
								level: 0,
							});
						} else {
							console.error(
								'Skipping unresolved node because parent has no position:',
								nodeId
							);
						}
					}
				}

				const { node, x, y, level } = queue.shift()!;

				// Avoid processing the same node multiple times
				if (node?.position?.x !== null && node?.position?.y !== null) {
					console.warn(
						'Node already processed, skipping:',)
					continue;
				}

				// Assign position
				node.position.x = x;
				node.position.y = y;

				// Process children
				if (node.children.length > 0) {
					const totalChildren = node.children.length;
					const childSpacing = totalChildren > 1 ? OFFSET_X : 0;

					node.children.forEach((childNode, index) => {
						let childX: number;

						// Custom positioning for first-level children
						if (level === 0) {
							childX = index === 0 ? x - 200 : x + 200;
						} else {
							childX =
								x +
								(index - Math.floor(totalChildren / 2)) *
									childSpacing;
						}

						if (childNode.type === 'department') {
							queue.push({
								node: childNode,
								x:
									x >= 0
										? childX + OFFSET_X
										: childX - OFFSET_X,
								y: y + OFFSET_Y,
								level: level + 1,
							});
						} else {
							if (childNode.position?.x === null) {
								queue.push({
									node: childNode,
									x: childX,
									y: y + OFFSET_Y,
									level: level + 1,
								});
							}
						}
					});
				}
			}

			if(rootUser && rootUser._id) {
				//Remove root user from nodeMap (owner is handled separately in frontend)
				console.log('🗑️ Removing root user from nodeMap:', rootUser._id);
				delete nodeMap[rootUser._id];
			}

			const finalUsers = Object.values(nodeMap).filter(
				(node) => node.type === 'user'
			);
			
			console.log('📊 normalizeValues - Final result:', {
				usersCount: finalUsers.length,
				users: finalUsers.map(u => ({
					_id: u._id || u.user?._id,
					userId: u.user?._id,
					userEmail: u.user?.email,
					userName: u.user?.profile_information?.name,
					userDepartment: u.user?.profile_information?.department || u.department,
					type: u.type,
					hasUser: !!u.user,
					hasPosition: !!u.position,
					position: u.position,
					keys: Object.keys(u || {})
				})),
				sampleUserFull: finalUsers[0] ? {
					...finalUsers[0],
					// Don't log children array as it can be huge
					children: finalUsers[0].children?.length || 0
				} : null,
				departmentsCount: departments.length > 0
					? Object.values(nodeMap).filter((node) => node.type === 'department').length
					: 0
			});

			// Prepare results and resolve the promise
			resolve({
				users: finalUsers,
				departments:
					departments.length > 0
						? Object.values(nodeMap).filter(
								(node) => node.type === 'department'
							)
						: [], // Return empty array if departments is empty
			});
		} catch (error) {
			console.error('Error in normalizeValues function:', error);
			reject(error); // Reject the promise in case of error
		}
	});
}
